// export default Report;
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Service from '../services/Service';
import Overview from '../components/Overview/Overview';
import Progress from '../components/Progress/Progress';
import '../styles/Report.css';
import DetailedAnalysis from '../components/DetailedAnalysis/DetailedAnalysis';
import ReportExtraData from '../../ReportExtraData/ReportExtraData';
import AdminRoleReport from '../components/AdminRoleReport/AdminRoleReport';

const Report = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState('');
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [effectiveUserId, setEffectiveUserId] = useState(null);

  // Decode token
  useEffect(() => {
    const token = sessionStorage.getItem('auth_token');
    const passedUserId = location?.state?.userId || null;

    if (token) {
      try {
        const decoded = jwtDecode(token);
        setRole(decoded.role || null);
        setEffectiveUserId(passedUserId || decoded.user_id);
      } catch {
        setEffectiveUserId(passedUserId || null);
      }
    } else {
      setEffectiveUserId(passedUserId || null);
    }
  }, [location?.state?.userId]);

  const passedOrderId = location?.state?.orderId || null;

  const handleBack = () => navigate(-1);

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!effectiveUserId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await Service.getOrderList(effectiveUserId, { force: true });
        if (Array.isArray(response?.data)) setOrders(response.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [effectiveUserId, location.key]);

  // Auto-select last or passed order
  useEffect(() => {
    if (!loading && orders.length > 0) {
      let defaultOrder = passedOrderId ? orders.find((o) => o.id === passedOrderId) : orders[orders.length - 1];

      if (defaultOrder) {
        setSelectedOrder(String(defaultOrder.order_number));
        setSelectedOrderDetails(defaultOrder);
      }
    }
  }, [loading, orders, passedOrderId]);

  const handleOrderChange = (e) => {
    const orderNo = e.target.value;
    setSelectedOrder(orderNo);
    const found = orders.find((o) => String(o.order_number) === orderNo);
    setSelectedOrderDetails(found || null);
  };

  // ---- Metric builder (no changes)
  const getMetricsFromCategories = (categories) => {
    if (!categories) return [];
    const safeNum = (v) => (Number.isFinite(+v) ? +v : 0);

    const motility = categories?.motility?.metrics || [];
    const morphology = categories?.morphology?.metrics || [];
    const concentration = categories?.concentration?.metrics || [];

    const progressive = safeNum(motility.find((m) => m.name === 'Progressive motility')?.value);
    const overallMotility = safeNum(motility.find((m) => m.name === 'Overall motility')?.value);
    const immotile = safeNum(motility.find((m) => m.name === 'Immotile')?.value);
    const morph = safeNum(morphology.find((m) => m.name === 'Normal forms')?.value);
    const conc = safeNum(concentration.find((m) => m.name === 'Concentration')?.value);
    const funcSpermCount = safeNum(concentration.find((m) => m.name === 'Functional sperm count')?.value);

    const WHO = {
      tmc: 20,
      progressive: 32,
      morph: 4,
      immotile: 20,
      concentration: 15,
      vitality: 58,
    };

    return [
      {
        name: 'TOTAL FUNCTIONAL SPERM(TOTAL MOTILE COUNT)',
        value: funcSpermCount,
        actualValue: funcSpermCount, // Latest current test value, not average
        unit: 'million',
        whoValue: WHO.tmc,
        score: Math.min(100, (funcSpermCount / WHO.tmc) * 100),
      },
      {
        name: 'SWIMMING FORWARD(PROGRESSIVE MOTALITY)',
        value: progressive,
        actualValue: progressive, // Latest current test value, not average
        unit: '%',
        whoValue: WHO.progressive,
        score: Math.min(100, (progressive / WHO.progressive) * 100),
      },
      {
        name: 'NORMAL-SHAPED(MORPHOLOGY)',
        value: morph,
        actualValue: morph, // Latest current test value, not average
        unit: '%',
        whoValue: WHO.morph,
        score: Math.min(100, (morph / WHO.morph) * 100),
      },
      {
        name: 'DNA INTEGRITY(DNA FRAGMENTATION',
        value: immotile,
        actualValue: immotile, // Latest current test value, not average
        unit: '%',
        whoValue: WHO.immotile,
        isInverted: true,
        score: Math.max(0, 100 - (immotile / WHO.immotile) * 100),
      },
      {
        name: 'SPERM COUNT(CONCENTRATION)',
        value: conc,
        actualValue: conc, // Latest current test value, not average
        unit: 'million/mL',
        whoValue: WHO.concentration,
        score: Math.min(100, (conc / WHO.concentration) * 100),
      },
      {
        name: 'LIVING SPERM(VITALITY)',
        value: overallMotility,
        actualValue: overallMotility, // Latest current test value, not average
        unit: '%',
        whoValue: WHO.vitality,
        score: Math.min(100, (overallMotility / WHO.vitality) * 100),
      },
    ];
  };

  // Extract test data
  const testHistory = selectedOrderDetails?.db_order?.pdf_documents?.[0]?.parsed_data?.[0]?.data?.testHistory || [];

  const currentTestData = testHistory[testHistory.length - 1] || null;
  const previousTestData = testHistory.length > 1 ? testHistory[testHistory.length - 2] : null;

  const status = selectedOrderDetails?.db_order?.status_detail?.label;
  const isTestComplete = status === 'Test Complete' || status === 'Order Processing';

  let currentTest = null;
  if (currentTestData) {
    const scoreInfo = selectedOrderDetails?.db_order?.swimscore_overall_score;

    currentTest = {
      date: currentTestData.date,
      overallScore: scoreInfo,
      rawSwimScore: scoreInfo,
      trend: null,
      keyMetrics: getMetricsFromCategories(currentTestData?.categories),
    };
  }

  // Only show actual name to admins
  const displayName =
    role === 'admin' ? selectedOrderDetails?.customer?.first_name + ' ' + selectedOrderDetails?.customer?.last_name || 'User' : 'User';

  const userData = currentTestData
    ? {
        name: displayName,
        testHistory: [currentTestData],
      }
    : { testHistory: [] };

  return (
    <div className="report-container">
      {/* <p style={{ marginTop: -8, marginBottom: 16 }}>
        <strong>Report for:</strong> {displayName}
      </p> */}
      <button className="back-button" onClick={handleBack}>
        ← Back
      </button>
      <h1 className="report-title">Patient Report {displayName}</h1>
      {/* ⭐ ADMIN SHOULD SEE ADMIN REPORT ALSO */}
      {/* Dropdown */}
      <div className="dropdown-container">
        <label htmlFor="orderSelect" className="dropdown-label">
          Select an Order:
        </label>

        {loading ? (
          <p>Loading orders...</p>
        ) : (
          <select id="orderSelect" value={selectedOrder} onChange={handleOrderChange} className="order-dropdown">
            <option value="">-- Select Order --</option>
            {orders.map((order) => (
              <option key={order.id} value={String(order.order_number)}>
                #{order.order_number}
              </option>
            ))}
          </select>
        )}
      </div>
      {/* Order Info */}
      {selectedOrderDetails && (
        <div className="order-info">
          <p>
            <strong>Order Number:</strong> #{selectedOrderDetails.order_number}
          </p>
          <p>
            <strong>Status:</strong> {status || 'N/A'}
          </p>
        </div>
      )}
      {/* Display scoring */}
      {isTestComplete && currentTest ? (
        <div className="overview-section">
          <Overview currentTest={currentTest} previousTest={previousTestData} userName={selectedOrderDetails?.customer?.first_name || 'User'} />

          <div className="progress-section">
            <Progress userData={userData} />
          </div>

          {/* ⭐ ADMIN ONLY SEES FULL DETAILS */}

          <>
            <DetailedAnalysis userData={userData} />
            <ReportExtraData userData={userData} />
          </>
        </div>
      ) : (
        !loading && <p className="no-data">Your test data is still processing...</p>
      )}
      {role === 'admin' && (
        <AdminRoleReport
          orders={orders}
          loading={loading}
          selectedOrder={selectedOrder}
          handleOrderChange={handleOrderChange}
          selectedOrderDetails={selectedOrderDetails}
          onBack={handleBack}
        />
      )}
    </div>
  );
};

export default Report;
